'use client';

import { Link } from 'react-router-dom';
import { ArrowLeft, Info, Target, Eye, Heart, Building2, Users, Newspaper } from 'lucide-react';
import Footer from '@/components/Footer';


export default function SobrePage() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900 selection:bg-blue-200 selection:text-blue-900">
      {/* Header */}
      <header className="bg-gradient-to-r from-blue-600 to-indigo-700 text-white shadow-lg">
        <div className="max-w-4xl mx-auto px-4 py-12 md:py-20 text-center space-y-6">
          <Link 
            to="/" 
            className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 backdrop-blur-sm border border-white/30 px-6 py-3 rounded-2xl text-white font-black uppercase tracking-tighter italic transition-all hover:-translate-x-1 active:scale-95 mb-8 group shadow-lg"
          >
            <ArrowLeft className="w-5 h-5 group-hover:-translate-x-1 transition-transform" />
            Voltar para o Início
          </Link>
          <h1 className="text-3xl md:text-5xl font-black tracking-tighter uppercase italic">
            Sobre o Portal <br className="md:hidden" />
            <span className="text-blue-200">Catanduvas PR</span>
          </h1>
          <p className="text-blue-100 text-lg md:text-xl font-medium max-w-2xl mx-auto">
            Conheça a história, a missão e quem está por trás do principal portal de utilidade pública da nossa cidade.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 -mt-8 pb-20">
        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 shadow-2xl border border-slate-100 space-y-16">
          
          {/* Nossa História */}
          <section className="space-y-6">
            <div className="flex items-center gap-3">
              <div className="bg-blue-100 p-3 rounded-2xl">
                <Info className="w-8 h-8 text-blue-600" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">Nossa História</h2>
            </div>
            <div className="prose prose-slate max-w-none text-slate-600 font-medium leading-relaxed">
              <p>
                O portal <strong>CatanduvasPR.com.br</strong> nasceu da necessidade de centralizar informações essenciais para os moradores e visitantes de Catanduvas, no Paraná. Em um mundo cada vez mais digital, percebemos que dados simples como horários de ônibus, telefones úteis e plantões de farmácias estavam dispersos ou eram de difícil acesso.
              </p>
              <p>
                Desde o seu lançamento, o portal se consolidou como a principal ferramenta de consulta rápida da cidade, servindo como uma ponte entre os serviços públicos, o comércio local e o cidadão.
              </p>
            </div>
          </section>

          {/* Missão, Visão e Valores */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 space-y-4">
              <Target className="w-10 h-10 text-blue-600" />
              <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter italic">Missão</h3>
              <p className="text-slate-500 text-sm font-bold leading-relaxed">
                Facilitar a vida do cidadão catanduvense através da disponibilização de informações úteis, precisas e atualizadas em um único lugar.
              </p>
            </div>
            <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 space-y-4">
              <Eye className="w-10 h-10 text-indigo-600" />
              <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter italic">Visão</h3>
              <p className="text-slate-500 text-sm font-bold leading-relaxed">
                Ser a referência digital número um em utilidade pública e guia comercial para toda a região da Cantuquiriguaçu.
              </p>
            </div>
            <div className="bg-slate-50 p-8 rounded-[2rem] border border-slate-100 space-y-4">
              <Heart className="w-10 h-10 text-red-600" />
              <h3 className="text-xl font-black text-slate-800 uppercase tracking-tighter italic">Valores</h3>
              <p className="text-slate-500 text-sm font-bold leading-relaxed">
                Independência editorial, compromisso com a verdade, foco na utilidade prática e valorização do comércio local.
              </p>
            </div>
          </div>

          {/* Independência e Administração */}
          <section className="space-y-6 p-8 bg-blue-50 rounded-[2rem] border border-blue-100">
            <div className="flex items-center gap-3">
              <div className="bg-blue-600 p-3 rounded-2xl">
                <Building2 className="w-8 h-8 text-white" />
              </div>
              <h2 className="text-2xl font-black text-slate-800 uppercase tracking-tighter italic">Administração</h2>
            </div>
            <div className="text-slate-600 font-medium leading-relaxed space-y-4">
              <p>
                É importante ressaltar que o <strong>CatanduvasPR.com.br</strong> é um portal de <strong>utilidade pública independente</strong>. Não possuímos vínculo direto com a Prefeitura Municipal ou qualquer órgão governamental.
              </p>
              <p>
                O portal é integralmente produzido e administrado pelo <strong>Estúdio Mídia Publicidades</strong>, uma empresa comprometida com a comunicação digital e o desenvolvimento tecnológico da nossa região.
              </p>
              <div className="pt-4">
                <a 
                  href="https://estudiomidia.com.br/" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-blue-600 text-white px-8 py-4 rounded-2xl font-black uppercase tracking-tighter italic transition-all hover:scale-105 active:scale-95 shadow-lg shadow-blue-200"
                >
                  Visitar Estúdio Mídia
                </a>
              </div>
            </div>
          </section>

          {/* O que oferecemos */}
          <section className="space-y-8">
            <div className="text-center">
              <h2 className="text-3xl font-black text-slate-800 uppercase tracking-tighter italic">O que você encontra aqui</h2>
              <p className="text-slate-500 font-bold">Tudo o que você precisa em um clique</p>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { icon: <Users />, label: "Telefones Úteis" },
                { icon: <Newspaper />, label: "Notícias Locais" },
                { icon: <Building2 />, label: "Guia Comercial" },
                { icon: <Info />, label: "Plantão Farmácias" },
              ].map((item, i) => (
                <div key={i} className="flex flex-col items-center justify-center p-6 bg-white border border-slate-100 rounded-3xl shadow-sm hover:shadow-md transition-shadow">
                  <div className="text-blue-600 mb-3">{item.icon}</div>
                  <span className="text-xs font-black uppercase tracking-widest text-slate-400 text-center">{item.label}</span>
                </div>
              ))}
            </div>
          </section>

        </div>
      </main>

      <Footer />
    </div>
  );
}
